/**
 * Boards over MCP — the write half (phase 2, tranche 1).
 *
 * It lives beside `boards.js` rather than inside it because `boards.js`'s
 * header is the design record for why writes were held back, and because the
 * read surface and the write surface fail in different ways.
 *
 * ## Four rules, each of which prevents a specific, verified data loss
 *
 * 1. **Record-granular, always.** Every write here is one
 *    `extension_records_cas_upsert`. The app's own whole-board writer goes
 *    through `extension_records_sync_scope`, which tombstones every record in
 *    the scope absent from the array it is handed — so a read-modify-write from
 *    an agent working off a stale read would delete tasks a human added ten
 *    seconds ago. A test forbids this directory from even naming that path.
 *
 * 2. **Read-modify-write on the record's OWN data, never a rebuild from the
 *    arguments.** `data` is a full replace. Building it from what the caller
 *    sent drops `comments` (destroying them), `labels` (the chips vanish and
 *    the filter bar can never match the card again) and `position` (the card
 *    jumps to the top of its column on every client). See `boardRecords.js`.
 *
 * 3. **A write into a scope we cannot confirm is REFUSED, never redirected.**
 *    `scopeKeyFor('team:99')` happily returns `"team:99"` for any string, so an
 *    agent naming a team the user is not in — or naming a real team in the
 *    seconds before the background teams fetch lands — would write rows into a
 *    workspace that never syncs and never renders. Those rows are not lost,
 *    they are invisible, which is worse.
 *
 * 4. **A viewer on a team board is refused.** The server rejects a viewer's
 *    push, and a server-rejected push poisons that scope's outbox permanently
 *    with no event reaching JS — unrecoverable from the client. The board UI
 *    already mirrors the server rule; this is the same mirror, through the same
 *    predicate (`boardRoles.isViewerRole`), for the writer that has no UI.
 *
 * ## What is deliberately absent
 *
 * Deletion lives in `boardDeletes.js`, beside this file and behind the
 * destructive switch — it fails differently again (there is no undo), so it
 * gets its own module and its own header. Rules 3 and 4 above are shared with
 * it through `mcp/writeScope.js`; rules 1 and 2 do not apply to a tombstone.
 *
 * Also absent here: creating or editing the label palette; assignee and owner;
 * moving a board between the personal and a team scope; editing or deleting a
 * comment (`mergeComments` encodes a comment delete as *absence plus a newer
 * `updatedAt`* — an encoding no external client can be trusted with).
 *
 * An MCP comment can never notify anyone, and that is structural rather than a
 * guard: `buildComment` computes `mentions: resolveMentions(body, picked)`, and
 * `resolveMentions` only keeps ids present in an explicit `picked` array, so
 * `picked: []` yields `[]` whatever the body says.
 */
import { userIdOfScope } from '../../extensions/bundled/task-manager/boardCreatedBy';
import { normalizeBoardColor } from '../../extensions/bundled/task-manager/boardColorValue';
import { aiCreatedMarks, mcpSource } from '../../services/ai/aiAuthorship';
import { boardUri } from '../resources';
import { PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { capRefusalFor } from '../../plans/capRefusal';
import { countOwnBoardsAndTasks } from '../../extensions/bundled/task-manager/boardsStore';
import { scopeReport } from '../writeScope';
import { stageBoardRecords } from '../boardChanges';
import {
  newId,
  nowIso,
  boardRecordFor,
  groupRecordFor,
  patchedRecord,
  nextGroupPosition,
  toWireRecord,
} from '../boardRecords';
import {
  MAX_LISTS_ON_CREATE,
  VIEW_MODES,
  boardRows,
  casWrite,
  checkPosition,
  findBoard,
  findGroup,
  requireRev,
  targetScope,
} from './boardWriteChecks';
export { boardRows, findBoard, findGroup, findTask, fromBoardRustError, targetScope } from './boardWriteChecks';
export { boardTaskCreate, boardTaskMove, boardTaskUpdate } from './boardTaskWrites';
export { boardCommentAdd } from './boardCommentWrites';

// ── the tools ──────────────────────────────────────────────────────────────

/**
 * Create a board, and optionally its lists, in the personal scope.
 *
 * PERSONAL ONLY, and the reason is not squeamishness: the app follows a team
 * board create with `teamsService.shareBoardWithTeam(...)` so the Team page
 * lists it, and that is an authenticated HTTP call through the app's API
 * client, which is pinned as unreachable from this module graph. A team board
 * created without it would sync its content and be missing from the Team page.
 * A half-created board is worse than a refusal.
 *
 * The BOARD record is written first, so a failure part-way leaves a reachable
 * board with fewer lists rather than orphaned lists under nothing.
 */
export async function boardCreate({ name, scope, description, color, lists }, ctx = {}) {
  const wire = scope || PERSONAL;
  if (wire !== PERSONAL) {
    return mcpError(
      'invalid_argument',
      'board_create makes personal boards only. A team board also needs an ownership record on the Team page that is not reachable over MCP, and a board that syncs but never appears there is worse than a refusal.',
      { field: 'scope' }
    );
  }
  // Plans v2: personal boards count against the plan's local cap.
  const cappedBoards = await capRefusalFor('boards', async () => (await countOwnBoardsAndTasks()).boards);
  if (cappedBoards) return mcpError('plan_limit', cappedBoards, { requiredPlan: 'basic' });
  const title = String(name ?? '').trim();
  if (!title) {
    return mcpError('invalid_argument', 'name must not be empty.', { field: 'name' });
  }
  if (lists !== undefined && !Array.isArray(lists)) {
    return mcpError('invalid_argument', 'lists must be an array of list names.', {
      field: 'lists',
    });
  }
  const listNames = (lists || []).map((n) => String(n ?? '').trim()).filter(Boolean);
  if (listNames.length > MAX_LISTS_ON_CREATE) {
    return mcpError(
      'invalid_argument',
      `A board can be created with at most ${MAX_LISTS_ON_CREATE} lists. Add the rest with board_list_create.`,
      { field: 'lists', max: MAX_LISTS_ON_CREATE }
    );
  }

  const target = targetScope(wire);
  if (target.error) return target.error;

  const now = nowIso();
  const boardId = newId();
  const boardRecord = boardRecordFor({
    id: boardId,
    name: title,
    description: description ? String(description) : '',
    color: normalizeBoardColor(color),
    viewMode: null,
    showDoneTasks: true,
    labels: [],
    visibility: 'private',
    teamId: null,
    // A personal (or this-device) board: its scope names the user (extras §2.8).
    createdBy: userIdOfScope(target.scopeKey),
    createdAt: now,
    updatedAt: now,
    // AI Act Art. 50(2): made by an external agent (services/ai/aiAuthorship.js).
    ...aiCreatedMarks(undefined, mcpSource(ctx?.caller)),
  });
  const head = await casWrite(target.scopeKey, boardRecord, null);
  if (head.error) return head.error;

  const written = [boardRecord];
  const created = [];
  const failed = [];
  for (let i = 0; i < listNames.length; i += 1) {
    const group = { id: newId(), name: listNames[i], position: i };
    const record = groupRecordFor(boardId, group);
    // eslint-disable-next-line no-await-in-loop
    const out = await casWrite(target.scopeKey, record, null);
    if (out.error) {
      failed.push(listNames[i]);
      continue;
    }
    written.push(record);
    created.push({ id: group.id, name: group.name, position: group.position, rev: out.rev });
  }

  // A board with no lists is a legitimate result and renders correctly — it is
  // exactly what the app's own create modal writes (`groups: [], tasks: []`),
  // and the board shows its "Add list" affordance.
  const result = {
    id: boardId,
    name: title,
    scope: wire,
    uri: boardUri(wire, boardId),
    rev: head.rev,
    lists: created,
    // Reported rather than thrown: the board exists and is usable, and the
    // model can add the missing lists with board_list_create. A failure here
    // would hide a board that was in fact created.
    complete: failed.length === 0,
    ...(failed.length ? { failedLists: failed } : {}),
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, written.map(toWireRecord));
}

/**
 * Rename a board or change its display settings.
 *
 * Read-modify-write on the board record's own `data`, so `labels`,
 * `visibility`, `teamId` and `createdAt` are carried forward verbatim. None of
 * those is settable here: rewriting `labels` from an argument would drop every
 * field of every label it did not restate, and changing `visibility`/`teamId`
 * is a data-residency change that is out of scope at any grant level.
 */
export async function boardUpdate({
  board_id: boardId,
  expected_rev: expectedRev,
  scope,
  name,
  description,
  color,
  view_mode: viewMode,
  show_done_tasks: showDoneTasks,
  show_column_progress: showColumnProgress,
}) {
  const wire = scope || PERSONAL;
  const missingRev = requireRev(expectedRev);
  if (missingRev) return missingRev;
  if (viewMode !== undefined && !VIEW_MODES.includes(viewMode)) {
    return mcpError('invalid_argument', 'view_mode must be kanban or list.', {
      field: 'view_mode',
    });
  }
  if (name !== undefined && !String(name).trim()) {
    return mcpError('invalid_argument', 'name must not be empty.', { field: 'name' });
  }

  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');

  const patch = { updatedAt: nowIso() };
  if (name !== undefined) patch.name = String(name).trim();
  if (description !== undefined) patch.description = String(description);
  if (color !== undefined) patch.color = normalizeBoardColor(color);
  if (viewMode !== undefined) patch.viewMode = viewMode;
  if (showDoneTasks !== undefined) patch.showDoneTasks = !!showDoneTasks;
  if (showColumnProgress !== undefined) patch.showColumnProgress = !!showColumnProgress;

  const record = patchedRecord(boardRow, { data: patch });
  const out = await casWrite(target.scopeKey, record, expectedRev);
  if (out.error) return out.error;

  const result = {
    id: boardRow.id,
    name: record.data.name,
    scope: wire,
    uri: boardUri(wire, boardRow.id),
    rev: out.rev,
    ...scopeReport(wire),
  };
  // `patchBoardWithRecords` deliberately ignores board-level records (a
  // delete's payload cannot always be told apart from a group's), so this one
  // lands through the nonce's disk re-read rather than in memory. Staged
  // anyway, so the shape is uniform and a future patcher needs no new call.
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}

/** Add a list (a kanban column) to an existing board. */
export async function boardListCreate({ board_id: boardId, name, scope, position }) {
  const wire = scope || PERSONAL;
  const listName = String(name ?? '');
  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');

  const groups = rows.filter((r) => r.record_type === 'group');
  const badPosition = checkPosition(position, nextGroupPosition(groups));
  if (badPosition) return badPosition;

  const group = {
    id: newId(),
    name: listName,
    position: position === undefined ? nextGroupPosition(groups) : position,
  };
  const record = groupRecordFor(boardRow.id, group);
  const out = await casWrite(target.scopeKey, record, null);
  if (out.error) return out.error;

  const result = {
    boardId: boardRow.id,
    id: group.id,
    name: group.name,
    position: group.position,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}

/** Rename one list. `expected_rev` required — a list name is prose. */
export async function boardListRename({
  board_id: boardId,
  list_id: listId,
  name,
  expected_rev: expectedRev,
  scope,
}) {
  const wire = scope || PERSONAL;
  const missingRev = requireRev(expectedRev);
  if (missingRev) return missingRev;

  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const groupRow = findGroup(rows, listId);
  if (!groupRow) return mcpError('not_found', 'No list with that id on this board.');
  if (groupRow.parent_id !== boardRow.id) {
    return mcpError('invalid_parent', 'That list belongs to a different board.', {
      boardId: boardRow.id,
      listId: groupRow.id,
    });
  }

  const record = patchedRecord(groupRow, { data: { name: String(name ?? '') } });
  const out = await casWrite(target.scopeKey, record, expectedRev);
  if (out.error) return out.error;

  const result = {
    boardId: boardRow.id,
    id: groupRow.id,
    name: record.data.name,
    scope: wire,
    rev: out.rev,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [toWireRecord(record)]);
}

